import type { PreOrderFlow } from "@/app/api/types/preOrder";
import type { PreOrderFlow as OfflinePreOrderFlow } from "@stockline/shared/types/orders/pre-order-flow";
import type { PreOrderFlowChange } from "@stockline/web";
import { toOfflineFlowData } from "./modifyBatch";
import { PreOrderFlowStatus } from "@/app/api/types/preOrderFlow";

/**
 * Feature-flag-derived configuration indicating which flow columns are
 * invisible to the user. When a column is disabled the user cannot see
 * or edit it, so its value must be null — otherwise invisible data
 * leaks to ERPs (M3/AS400/Sage) on export.
 */
export interface DisabledColumnsConfig {
  /** `showUnitRecap === false` → `unit_id` must be null. */
  unitDisabled: boolean;
  /** `showWeightRecap === false` → `weight` must be null. */
  weightDisabled: boolean;
  /** `showPricesRecap === false` → `price_per_weight` must be null. */
  priceDisabled: boolean;
  /** `export_mode !== "vivo"` → `flow_metadata.m3_comments` must be null. */
  m3CommentsDisabled: boolean;
}

/**
 * Build push-batch `update` ops that null out disabled-column values
 * for flows where those values are currently non-null.
 *
 * Only emits ops for flows that actually need a change — callers can
 * skip the push when the result is empty.
 */
export function buildSanitizeOps(
  flows: ReadonlyArray<PreOrderFlow>,
  config: DisabledColumnsConfig,
): PreOrderFlowChange[] {
  if (!hasAnyDisabled(config)) return [];

  const ops: PreOrderFlowChange[] = [];
  for (const flow of flows) {
    if (flow.status === PreOrderFlowStatus.CANCELLED) continue;
    const patch = buildFlowPatch(flow, config);
    if (patch) {
      ops.push({ op: "update", id: flow.id, data: toOfflineFlowData(patch) });
    }
  }
  return ops;
}

function buildFlowPatch(
  flow: PreOrderFlow,
  config: DisabledColumnsConfig,
): Partial<PreOrderFlow> | null {
  const patch: Partial<PreOrderFlow> = {};
  let needsUpdate = false;

  if (config.unitDisabled && flow.unit_id != null) {
    patch.unit_id = null;
    needsUpdate = true;
  }
  if (config.weightDisabled && flow.weight != null) {
    patch.weight = null;
    needsUpdate = true;
  }
  if (config.priceDisabled && flow.price_per_weight != null) {
    patch.price_per_weight = null;
    needsUpdate = true;
  }
  if (config.m3CommentsDisabled && flow.flow_metadata?.m3_comments != null) {
    const { m3_comments: _, ...rest } = flow.flow_metadata!;
    patch.flow_metadata = Object.keys(rest).length > 0 ? rest : null;
    needsUpdate = true;
  }

  return needsUpdate ? patch : null;
}

/** True when at least one column is disabled. */
function hasAnyDisabled(config: DisabledColumnsConfig): boolean {
  return (
    config.unitDisabled ||
    config.weightDisabled ||
    config.priceDisabled ||
    config.m3CommentsDisabled
  );
}

/**
 * Merge two partial flow data objects, deep-merging `flowMetadata` so
 * user edits to custom columns (Rolls, Pallets, …) survive when the
 * sanitize op strips `m3_comments` from the same JSONB lane.
 */
function mergeFlowData(
  userData: Partial<OfflinePreOrderFlow>,
  sanitizeData: Partial<OfflinePreOrderFlow>,
): Partial<OfflinePreOrderFlow> {
  const merged: Partial<OfflinePreOrderFlow> = { ...userData, ...sanitizeData };

  const userFm = userData.flowMetadata as Record<string, unknown> | null;
  const sanitizeFm = sanitizeData.flowMetadata as Record<
    string,
    unknown
  > | null;

  if (userFm != null && sanitizeFm != null) {
    // Union: user values win for shared keys, then strip only the disabled
    // metadata key(s). We must NOT intersect against sanitizeFm as an
    // allowlist — that would silently drop new keys the user just added
    // (e.g. a custom column value typed for the first time).
    const result: Record<string, unknown> = { ...sanitizeFm, ...userFm };
    if (!("m3_comments" in sanitizeFm)) {
      delete result.m3_comments;
    }
    merged.flowMetadata = Object.keys(result).length > 0 ? result : null;
  } else if (userFm != null && sanitizeFm === null) {
    // sanitizeFm===null means the live flow had ONLY m3_comments (nothing
    // else survived stripping). Keep user keys except m3_comments.
    const result: Record<string, unknown> = { ...userFm };
    delete result.m3_comments;
    merged.flowMetadata = Object.keys(result).length > 0 ? result : null;
  }

  return merged;
}

/**
 * Augment an existing `flowChanges` array with sanitization ops for
 * disabled columns. Existing ops for the same flow id are merged
 * (sanitize values applied on top) so we never duplicate an update.
 */
export function mergeWithSanitizeOps(
  existingOps: PreOrderFlowChange[],
  sanitizeOps: PreOrderFlowChange[],
): PreOrderFlowChange[] {
  if (sanitizeOps.length === 0) return existingOps;

  const sanitizeMap = new Map(
    sanitizeOps
      .filter(
        (op): op is PreOrderFlowChange & { op: "update" } => op.op === "update",
      )
      .map((op) => [op.id, op.data]),
  );

  const seenIds = new Set<string>();
  const merged = existingOps.map((op) => {
    if (op.op === "delete") {
      seenIds.add(op.id);
      return op;
    }

    const sanitizeData = sanitizeMap.get(op.id);
    if (!sanitizeData) return op;
    seenIds.add(op.id);

    return {
      ...op,
      data: mergeFlowData(
        op.data as Partial<OfflinePreOrderFlow>,
        sanitizeData as Partial<OfflinePreOrderFlow>,
      ),
    } as PreOrderFlowChange;
  });

  for (const [id, data] of sanitizeMap) {
    if (!seenIds.has(id)) {
      merged.push({ op: "update", id, data });
    }
  }

  return merged;
}
